import { useEffect, useMemo, useRef, useState } from "react";
import { useStudioEditor } from "../../../store/studioEditorStore";
import ErrorMessage from "../../StudioErstellen/ErrorMessage";

import classes from "../../StudioErstellen/StudioEquipment/StudioEquipment.module.css";
import StudioEquipmentEditorPhoneBox from "./StudioEquipmentEditorPhoneBox";

const makeLocalItemId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
};

const makeEmptyItem = () => ({ name: "", count: 1, id: makeLocalItemId() });

const normalizeEquipmentMap = (equipment) => {
    if (!equipment || typeof equipment !== "object") {
        return {};
    }

    const normalized = {};

    // Backend shape in editor flow: ModelStudioEquipmentConnection
    // equipment.items = [{ category, items: [{ id, name, count }] }]
    if (Array.isArray(equipment?.items)) {
        equipment.items.forEach((categoryBlock) => {
            const category = categoryBlock?.category;
            if (!category || !Array.isArray(categoryBlock?.items)) {
                return;
            }

            normalized[category] = categoryBlock.items
                .filter((item) => item && typeof item === "object")
                .map((item, index) => ({
                    name: String(item.name || ""),
                    count: Math.max(1, Number(item.count || 1)),
                    id: String(item.id || `${category}_${index}_${makeLocalItemId()}`),
                }));
        });

        return normalized;
    }

    // Fallback for map shape: { [category]: [{ name, count, id }] }
    Object.entries(equipment).forEach(([category, items]) => {
        if (!Array.isArray(items)) return;
        normalized[category] = items
            .filter((item) => item && typeof item === "object")
            .map((item, index) => ({
                name: String(item.name || ""),
                count: Math.max(1, Number(item.count || 1)),
                id: String(item.id || `${category}_${index}_${makeLocalItemId()}`),
            }));
    });

    return normalized;
};

const getEquipmentCategories = (studioTypes) => {
    const types = Array.isArray(studioTypes) ? studioTypes : [];
    const hasMusik = types.includes("Musik");
    const hasFotoOrVideo = types.includes("Foto") || types.includes("Video");

    const photoVideoCategories = [
        "Lichtequipment",
        "Kameras und Objektive",
        "Stative",
        "Set-Accessoires",
        "Weiteres Equipment",
    ];

    const musikCategories = [
        "Aufnahme & Mikrofone",
        "Lautsprecher & Kopfhörer (Monitoring)",
        "Recorder",
        "Software & Computer",
        "Mischpulte & Steuerung",
        "Klangbearbeitung (Preamps & Effekte)",
        "Instrumente",
        "Akustik & Kabel",
        "Sonstiges Equipment",
    ];

    if (hasMusik && !hasFotoOrVideo) {
        return musikCategories;
    }

    if (hasMusik && hasFotoOrVideo) {
        return [...photoVideoCategories, ...musikCategories];
    }

    return photoVideoCategories;
};

const CategoryEditor = ({ category, items, setItemsForCategory }) => {
    const [isActive, setIsActive] = useState(items.length > 0);
    const [errors, setErrors] = useState({ name: "" });

    useEffect(() => {
        setIsActive(items.length > 0);
    }, [items.length]);

    const runValidation = (nextItems) => {
        const validItems = nextItems.filter((item) => item.name.trim() !== "");
        const names = validItems.map((item) => item.name.trim().toLowerCase());
        const hasDuplicate = new Set(names).size !== names.length;

        if (hasDuplicate) {
            setErrors({ name: "Name schon vorhanden." });
            return false;
        }

        setErrors({ name: "" });
        return true;
    };

    const handleToggle = (checked) => {
        setIsActive(checked);
        setErrors({ name: "" });

        if (!checked) {
            setItemsForCategory([]);
            return;
        }

        if (items.length === 0) {
            setItemsForCategory([makeEmptyItem()]);
        }
    };

    const handleUpdateItem = (id, field, value) => {
        const nextItems = items.map((item) => (item.id === id ? { ...item, [field]: value } : item));
        setItemsForCategory(nextItems);
        runValidation(nextItems);
    };

    const handleAddItem = () => {
        const nextItems = [...items, makeEmptyItem()];
        setItemsForCategory(nextItems);
    };

    const handleDeleteItem = (id) => {
        const nextItems = items.filter((item) => item.id !== id);
        setItemsForCategory(nextItems);
        runValidation(nextItems);
    };

    const lastItem = items[items.length - 1];
    const disableAdd = !lastItem || !lastItem.name.trim() || Boolean(errors.name);

    return (
        <div className={`${classes.equipmentBox} ${isActive ? classes.active : ""}`}>
            <div className={classes.header}>
                <span className="semiboldLarge">{category}</span>
                <div className={classes.switchContainer}>
                    <label className={classes.switch}>
                        <input type="checkbox" checked={isActive} onChange={(e) => handleToggle(e.target.checked)} />
                        <span className={classes.slider}></span>
                    </label>
                </div>
            </div>

            <div className={classes.expandableContent}>
                {isActive && (
                    <>
                        {items.map((item) => (
                            <div key={item.id} className={classes.equipmentItem}>
                                <div className={classes.insideInputContainer}>
                                    <input
                                        type="text"
                                        value={item.name}
                                        onChange={(e) => handleUpdateItem(item.id, "name", e.target.value)}
                                        placeholder="Name/Modell eingeben"
                                        className={classes.nameInput}
                                    />
                                    <div className={classes.countControl}>
                                        <button
                                            onClick={() => handleUpdateItem(item.id, "count", Math.max(1, item.count - 1))}
                                            className={classes.countButton}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="2" viewBox="0 0 13 2" fill="none">
                                                <path d="M0.196289 1.75V0.25H12.1963V1.75H0.196289Z" fill={`${item.count === 1 ? "#DEE1E5" : "#1F1F1F"}`} />
                                            </svg>
                                        </button>
                                        <span className={classes.count}>{item.count}</span>
                                        <button onClick={() => handleUpdateItem(item.id, "count", item.count + 1)} className={classes.countButton}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                                                <path d="M5.73283 12V0H7.25283V12H5.73283ZM0.479492 6.77333V5.25333H12.4795V6.77333H0.479492Z" fill="#1F1F1F" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <button className={classes.deleteButton} onClick={() => handleDeleteItem(item.id)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M7.2689 8.72266L0.201172 1.65492L1.13344 0.722656L8.20117 7.79039L15.2689 0.722656L16.2012 1.65492L9.13344 8.72266L16.2012 15.7904L15.2689 16.7227L8.20117 9.65493L1.13344 16.7227L0.201172 15.7904L7.2689 8.72266Z"
                                            fill="#696F76"
                                        />
                                    </svg>
                                </button>
                            </div>
                        ))}
                        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                        <button
                            className={`${classes.addMoreButton} semiboldXs ${disableAdd ? classes.disabled : ""}`}
                            disabled={disableAdd}
                            onClick={handleAddItem}
                        >
                            Weiteres {category} hinzufügen
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export const StudioEquipmentEditor = ({ studio, validate }) => {
    const { updateField, updatedFields } = useStudioEditor();
    const initializedRef = useRef(false);
    const initializedStudioIdRef = useRef(null);
    const hasLocalChangesRef = useRef(false);
    const updateFieldRef = useRef(updateField);
    const lastPayloadRef = useRef("");
    const [equipmentMap, setEquipmentMap] = useState({});

    const equipmentCategories = useMemo(
        () => getEquipmentCategories(studio?.studioTypes),
        [studio?.studioTypes]
    );

    useEffect(() => {
        const currentStudioId = studio?.id || null;
        const studioChanged = initializedStudioIdRef.current !== currentStudioId;

        const hasUpdatedEquipment = Object.prototype.hasOwnProperty.call(updatedFields, "equipment");

        if (studioChanged) {
            initializedStudioIdRef.current = currentStudioId;
            hasLocalChangesRef.current = hasUpdatedEquipment;
            initializedRef.current = false;
            lastPayloadRef.current = "";
        }

        // Hydrate from backend only once per studio id, or until user starts editing.
        if (!initializedRef.current && !hasLocalChangesRef.current) {
            const baseEquipment = normalizeEquipmentMap(hasUpdatedEquipment ? updatedFields.equipment : studio?.equipment);
            setEquipmentMap(baseEquipment);
            initializedRef.current = true;
        } else if (!initializedRef.current && hasUpdatedEquipment) {
            const baseEquipment = normalizeEquipmentMap(updatedFields.equipment);
            setEquipmentMap(baseEquipment);
            initializedRef.current = true;
        }
        // Rehydrate from updatedFields on remount; user edits update local state directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studio?.id, studio?.equipment]);

    useEffect(() => {
        updateFieldRef.current = updateField;
    }, [updateField]);

    useEffect(() => {
        if (!initializedRef.current || !hasLocalChangesRef.current) {
            return;
        }

        const cleanedEquipment = {};
        Object.entries(equipmentMap).forEach(([key, items]) => {
            const validItems = (items || [])
                .filter((item) => item.name.trim() !== "")
                .map((item) => ({
                    id: String(item.id || makeLocalItemId()),
                    name: item.name.trim(),
                    count: Math.max(1, Number(item.count || 1)),
                }));

            if (validItems.length > 0) {
                cleanedEquipment[key] = validItems;
            }
        });

        const payloadSignature = JSON.stringify(cleanedEquipment);
        if (lastPayloadRef.current === payloadSignature) {
            return;
        }

        lastPayloadRef.current = payloadSignature;
        updateFieldRef.current("equipment", cleanedEquipment);
    }, [equipmentMap]);

    useEffect(() => {
        if (!validate) {
            return;
        }

        validate.current = () => {
            const items = Object.values(equipmentMap).flat();
            const names = items
                .map((item) => item.name.trim().toLowerCase())
                .filter(Boolean);

            return new Set(names).size === names.length;
        };
    }, [equipmentMap, validate]);

    const setItemsForCategory = (category, items) => {
        hasLocalChangesRef.current = true;
        setEquipmentMap((prev) => ({
            ...prev,
            [category]: items,
        }));
    };

    return (
        <div className={classes.mainContainer} style={{ maxWidth: window.innerWidth < 742 ? "100%" : "429px" }}>
            <div className={classes.titleContainer}>
                <h2 className={`${classes.title} titleSemibold`} style={{ marginBottom: "30px" }}>
                    Equipment
                </h2>
            </div>
            <div className={classes.bottomContainer}>
                {equipmentCategories.map((category) => (
                    <CategoryEditor
                        key={category}
                        category={category}
                        items={equipmentMap[category] || []}
                        setItemsForCategory={(items) => setItemsForCategory(category, items)}
                    />
                ))}
            </div>

            <div className={classes.bottomContainerPhone}>
                {equipmentCategories.map(category => (
                    <StudioEquipmentEditorPhoneBox
                        key={category}
                        name={category}
                        items={equipmentMap[category] || []}
                        setItemsForCategory={(items) => setItemsForCategory(category, items)}
                    />
                ))}
            </div>
        </div>
    );
};

export default StudioEquipmentEditor;
